/**
 * EmailFieldWithValidation Component
 *
 * Reusable email input field with integrated real-time validation, typo suggestions,
 * and disposable email warnings. Provides comprehensive visual feedback and
 * accessibility features for optimal user experience.
 *
 * Features:
 * - Real-time email validation with debounced API calls
 * - Visual state indicators (success, warning, error, loading)
 * - Clickable typo suggestions for one-click correction
 * - Disposable email blocking with clear warnings
 * - ARIA live regions for screen reader announcements
 * - Mobile-friendly design with large touch targets
 * - Dark mode support
 * - Customizable validation options
 *
 * @example
 * <EmailFieldWithValidation
 *   value={email}
 *   onChange={setEmail}
 *   onValidationChange={(validation) => setIsValid(validation?.isValid)}
 *   required={true}
 *   strictMode={true}
 *   showSuggestions={true}
 *   className="mb-4"
 * />
 */

import { useState, useCallback, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { CheckCircle, XCircle, AlertCircle, Loader2 } from "lucide-react";
import { useEmailValidation } from "@shared/hooks";
import { cn } from "@shared/lib/utils";

/**
 * EmailFieldWithValidation Component
 *
 * @param {Object} props - Component props
 * @param {string} props.value - Current email value
 * @param {Function} props.onChange - Callback when email changes
 * @param {Function} [props.onValidationChange] - Callback when validation state changes
 * @param {boolean} [props.required=false] - Whether field is required
 * @param {boolean} [props.strictMode=true] - Block disposable emails and enforce MX checks
 * @param {boolean} [props.showSuggestions=true] - Show typo correction suggestions
 * @param {string} [props.label="Email Address"] - Field label
 * @param {string} [props.placeholder="your@email.com"] - Input placeholder
 * @param {string} [props.id="email"] - Input ID
 * @param {string} [props.name="email"] - Input name attribute
 * @param {string} [props.className=""] - Additional CSS classes
 * @param {boolean} [props.autoComplete=true] - Enable browser autocomplete
 * @param {boolean} [props.disabled=false] - Disable the input
 * @param {number} [props.debounceMs=500] - Validation debounce delay
 */
export default function EmailFieldWithValidation({
  value,
  onChange,
  onValidationChange,
  required = false,
  strictMode = true,
  showSuggestions = true,
  label = "Email Address",
  placeholder = "your@email.com",
  id = "email",
  name = "email",
  className = "",
  autoComplete = true,
  disabled = false,
  debounceMs = 500,
}) {
  const [_isFocused, setIsFocused] = useState(false);
  const [hasBlurred, setHasBlurred] = useState(false);
  const inputRef = useRef(null);

  // Initialize email validation hook
  const { validation, isValidating, validate, clearValidation } =
    useEmailValidation({
      checkMX: strictMode,
      checkTypos: showSuggestions,
      blockDisposable: strictMode,
      warnRole: true,
      debounceMs,
    });

  /**
   * Handle email input change
   */
  const handleChange = useCallback(
    (e) => {
      const newEmail = e.target.value;
      onChange(newEmail);

      // Trigger validation
      if (newEmail.trim()) {
        validate(newEmail);
      } else {
        clearValidation();
      }
    },
    [onChange, validate, clearValidation],
  );

  /**
   * Handle suggestion click - auto-correct email
   */
  const handleSuggestionClick = useCallback(() => {
    if (validation?.suggestion) {
      onChange(validation.suggestion);
      validate(validation.suggestion);

      // Refocus input for better UX
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  }, [validation, onChange, validate]);

  /**
   * Handle input focus
   */
  const handleFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  /**
   * Handle input blur
   */
  const handleBlur = useCallback(() => {
    setIsFocused(false);
    setHasBlurred(true);
  }, []);

  /**
   * Notify parent of validation changes
   */
  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(validation);
    }
  }, [validation, onValidationChange]);

  /**
   * Trigger validation on mount if value is already populated.
   * This handles autofilled emails, logged-in users, and any pre-filled values.
   */
  useEffect(() => {
    // Only validate if there's an initial value and validation hasn't run yet
    if (value && value.trim() && !validation && !isValidating) {
      validate(value);
    }
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Determine visual state
   */
  const getVisualState = () => {
    if (isValidating) return "validating";
    if (!value || !value.trim()) return "default";
    if (!hasBlurred && !validation) return "default";

    if (validation) {
      if (!validation.isValid) return "error";
      if (validation.suggestion && showSuggestions) return "suggestion";
      if (validation.isValid) return "success";
    }

    return "default";
  };

  const visualState = getVisualState();

  /**
   * Get border color based on state
   */
  const getBorderColor = () => {
    switch (visualState) {
      case "success":
        return "border-green-500 focus:ring-green-500";
      case "suggestion":
        return "border-amber-500 focus:ring-amber-500";
      case "error":
        return "border-red-500 focus:ring-red-500";
      case "validating":
        return "border-blue-400 focus:ring-blue-400";
      default:
        return "border-gray-300 dark:border-gray-700 focus:ring-primary-500";
    }
  };

  /**
   * Get icon component based on state
   */
  const getIcon = () => {
    switch (visualState) {
      case "success":
        return (
          <CheckCircle className="h-5 w-5 text-green-500" aria-hidden="true" />
        );
      case "suggestion":
        return (
          <AlertCircle className="h-5 w-5 text-amber-500" aria-hidden="true" />
        );
      case "error":
        return <XCircle className="h-5 w-5 text-red-500" aria-hidden="true" />;
      case "validating":
        return (
          <Loader2
            className="h-5 w-5 text-blue-500 animate-spin"
            aria-hidden="true"
          />
        );
      default:
        return null;
    }
  };

  /**
   * Get success message
   */
  const getSuccessMessage = () => {
    if (visualState !== "success" || !validation) return null;

    return (
      <p className="text-sm text-green-600 dark:text-green-400 mt-1">
        Email verified ✓
      </p>
    );
  };

  /**
   * Get suggestion message
   */
  const getSuggestionMessage = () => {
    if (visualState !== "suggestion" || !validation?.suggestion) return null;

    return (
      <div className="bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg mt-2 border border-amber-200 dark:border-amber-800">
        <p className="text-sm text-amber-800 dark:text-amber-200">
          Did you mean{" "}
          <button
            type="button"
            onClick={handleSuggestionClick}
            className="font-semibold underline hover:no-underline focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 rounded px-1"
            aria-label={`Use suggested email ${validation.suggestion}`}
          >
            {validation.suggestion}
          </button>
          ?
        </p>
      </div>
    );
  };

  /**
   * Get error message
   */
  const getErrorMessage = () => {
    if (visualState !== "error" || !validation?.errors?.length) return null;

    return (
      <div className="mt-2">
        {validation.errors.map((error, index) => (
          <p key={index} className="text-sm text-red-600 dark:text-red-400">
            {error}
          </p>
        ))}
      </div>
    );
  };

  /**
   * Get ARIA description
   */
  const getAriaDescription = () => {
    if (isValidating) return "Validating email address...";
    if (visualState === "success") return "Email address is valid";
    if (visualState === "suggestion" && validation?.suggestion) {
      return `Did you mean ${validation.suggestion}?`;
    }
    if (visualState === "error" && validation?.errors?.length) {
      return validation.errors.join(". ");
    }
    return "";
  };

  return (
    <div className={cn("w-full", className)}>
      {/* Label */}
      {label && (
        <label
          htmlFor={id}
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
        >
          {label}
          {required && (
            <span className="text-red-500 ml-1" aria-label="required">
              *
            </span>
          )}
        </label>
      )}

      {/* Input Container */}
      <div className="relative">
        <input
          ref={inputRef}
          type="email"
          id={id}
          name={name}
          value={value}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          autoComplete={autoComplete ? "email" : "off"}
          aria-invalid={visualState === "error"}
          aria-describedby={`${id}-validation`}
          className={cn(
            "w-full px-4 py-2 pr-12 border-2 rounded-lg transition-colors",
            "focus:outline-none focus:ring-2",
            "dark:bg-gray-800 dark:text-gray-100",
            "disabled:opacity-50 disabled:cursor-not-allowed",
            getBorderColor(),
          )}
        />

        {/* Icon */}
        {getIcon() && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
            {getIcon()}
          </div>
        )}
      </div>

      {/* ARIA Live Region for Screen Readers */}
      <div
        id={`${id}-validation`}
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {getAriaDescription()}
      </div>

      {/* Visual Feedback Messages */}
      {getSuccessMessage()}
      {getSuggestionMessage()}
      {getErrorMessage()}

      {/* Warnings (non-blocking) */}
      {validation?.warnings?.length > 0 && visualState !== "error" && (
        <div className="mt-2">
          {validation.warnings.map((warning, index) => (
            <p
              key={index}
              className="text-sm text-amber-600 dark:text-amber-400"
            >
              ⚠️ {warning}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

EmailFieldWithValidation.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onValidationChange: PropTypes.func,
  required: PropTypes.bool,
  strictMode: PropTypes.bool,
  showSuggestions: PropTypes.bool,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  id: PropTypes.string,
  name: PropTypes.string,
  className: PropTypes.string,
  autoComplete: PropTypes.bool,
  disabled: PropTypes.bool,
  debounceMs: PropTypes.number,
};
